<template>
  <div class="project-view-container">
    <!-- Upload Progress Overlay -->
    <div v-if="isUploading" class="upload-overlay">
      <div class="upload-modal">
        <div class="upload-spinner"></div>
        <h3>{{ uploadingMessage }}</h3>
        <p class="upload-progress-text">Please wait while files are being copied to the server...</p>
        <p class="upload-count">Uploading files to server, please do not close this window.</p>
      </div>
    </div>
    
    <div class="upload-document-header-container">
      <div class="breadcrumb-container">Upload Documents</div>
      <div class="upload-document-main-title">Upload Documents</div>
      <div>You may upload more documents here, please provide the project name.</div>
      <div v-if="enteredProjectId" class="documents-link-container">
        <router-link :to="`/projects/${enteredProjectId}/documents`" class="documents-link">
          📄 Looking for uploaded documents? Click here to view all documents →
        </router-link>
      </div>
    </div>
    <!-- Step #1: Input project details -->
    <div class="divider">
      <div class="accordion-panel-header">
        <label for="projectDetails"><strong>Project Details</strong></label>
        <ExpandCollapse name="projectDetails" :is-open-initially="isProjectDetailsOpen" :is-vertical-center="false"
          open-state="down" closed-state="up" :show-double-chevron="false" :show-circle="false"
          :chevron-fill="'#4A5FD5'" @toggle-expand-collapse="expandCollapseController('projectDetails');" />
      </div>
      <!-- Project Details -->
      <div v-if="isProjectDetailsOpen && allProjects.length > 0" class="project-view-form">
        <div class="tile existing-project">
          <div class="project-detail-header">Existing Project</div>
          <!-- Project Id -->
          <div class="custom-text-input label-text-spacing">
            <label class="left-div width-100">Project Id</label>
            <div class="project-id">
              <div v-if="enteredProjectId"><strong>({{ enteredProjectId }})</strong></div>
              <div v-else><strong>(No Project Selected)</strong></div>
            </div>
          </div>
          <!-- Project Name -->
          <div class="custom-text-input label-text-spacing">
            <label class="left-div width-100">Project Name</label>
            <div class="right-div">
              <auto-complete-search-box :items="allProjects" :display-key="'name'" :placeholder="'Enter the name of the project'" 
                @item-selected="updateSelectedProject($event)" @clear-search="clearSearch()"/>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Step #2: Input Baseline Documents -->
    <div>
      <div class="divider">
        <div class="accordion-panel-header">
          <label for="uploadBaselineDocuments"><strong>Upload Baseline Documents</strong></label>
          <ExpandCollapse name="uploadBaselineDocuments" :is-open-initially="isBaselineDocumentUpload"
            :is-vertical-center="false" open-state="down" closed-state="up" :show-double-chevron="false"
            :show-circle="false" :chevron-fill="'#4A5FD5'"
            @toggle-expand-collapse="expandCollapseController('baselineDocuments');" />
        </div>
      </div>
      <div class="tile" v-if="enteredProjectId && !isBaselineDocumentUpload">
        <UploadBaselineDocument ref="projectBaselineDocuments" @update:files="updateBaselineFiles"></UploadBaselineDocument>
        <!-- Submit Button -->
        <div class="project-action-button">
          <button @click="submitBaselineDocuments" :class="{ 'active-button': hasBaselineFiles }" :disabled="!hasBaselineFiles">Upload Documents</button>
        </div>
      </div>
    </div>

    <!-- Step #3: Input Draw Documents -->
    <div>
      <div class="divider">
        <div class="accordion-panel-header">
          <label for="uploadDrawDocuments"><strong>Upload Draw Documents</strong></label>
          <ExpandCollapse name="uploadDrawDocuments" :is-open-initially="isDrawDocumentUpload"
            :is-vertical-center="false" open-state="down" closed-state="up" :show-double-chevron="false"
            :show-circle="false" :chevron-fill="'#4A5FD5'"
            @toggle-expand-collapse="expandCollapseController('drawDocuments');" />
        </div>
      </div>
      <div class="tile" v-if="enteredProjectId && !isDrawDocumentUpload">
        <UploadDrawDocument ref="projectDrawDocuments" :project-id="enteredProjectId" @update:files="updateDrawFiles">
          <template v-slot:borrowerRequisitionLetter>
            <DropFile :header-title="'Borrower Requisition Letter'"
              :header-sub-title="'Download Template (Optional)'" :name="'borrowerRequisitionLetter'"
              v-on:uploaded-files="directFileUpdate($event, 'borrowerRequisitionLetter')" />
          </template>
        </UploadDrawDocument>
        <!-- Submit Button -->
        <div class="project-action-button">
          <button @click="submitDrawDocuments" :class="{ 'active-button': hasDrawFiles }" :disabled="!hasDrawFiles">Upload Draw Documents</button>
        </div>
      </div>
    </div>

    <!-- Step #4: List of Documents -->
    <div class="divider">
      <div class="accordion-panel-header">
        <label for="projectDocumentsList"><strong>Document List</strong></label>
        <ExpandCollapse name="projectDocumentsList" :is-open-initially="isProjectDocumentsListOpen"
          :is-vertical-center="false" open-state="down" closed-state="up" :show-double-chevron="false"
          :show-circle="false" :chevron-fill="'#4A5FD5'"
          @toggle-expand-collapse="expandCollapseController('projectDocumentsList')" />
      </div>
      <div class="tile" v-if="isProjectDocumentsListOpen">
        <ag-grid 
          ref="documentGrid"
          title="Documents" 
          :row-data="documentsList"
          :foreign-keys="{}" 
          :auth-token="authToken" 
          :full-scroll="true"
          :override-width="true" 
          :columnDefs="documentsColumnDefs" 
          :key="documentUploadCtr"
          @grid-ready="onDocumentGridReady" 
        />
      </div>
    </div>

    <!-- G703 Template Configuration Modal -->
    <G703TemplateModal 
      :visible="showG703TemplateModal"
      :project-id="enteredProjectId"
      :project-name="selectedProjectName"
      :sample-file-name="g703SampleFileName"
      @close="closeG703TemplateModal"
      @template-saved="handleG703TemplateSaved"
    />
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref, toRaw } from 'vue';
import { useRouter } from 'vue-router';
import { useAuthStore } from '@/stores/auth_store';
import { useErrorStore } from "@/stores/error_store";
import { axiosPostQuery, axiosQuery } from "@/assets/js/utils";
import axios from 'axios';
import ExpandCollapse from "@/components/helpers/ExpandCollapse.vue";
import UploadBaselineDocument from "@/shared/UploadBaselineDocument.vue";
import UploadDrawDocument from "@/shared/UploadDrawDocument.vue";
import AgGrid from "@/components/AgGrid.vue";
import AutoCompleteSearchBox from '@/components/add-project/AutoCompleteSearchBox.vue';
import G703TemplateModal from '@/components/G703TemplateModal.vue';
import { datadogRum } from '@datadog/browser-rum';

// Router for navigation
const router = useRouter();

/** Toggle Functions */
const isProjectDetailsOpen = ref(true);
const isBaselineDocumentUpload = ref(false);
const isDrawDocumentUpload = ref(false);
const isProjectDocumentsListOpen = ref(false);

// Function to navigate to document review page
function navigateToDocumentReview(documentId) {
  router.push(`/document-review/${documentId}`);
}

// Function to navigate to document classification page
function navigateToDocumentClassification(documentId) {
  router.push(`/document-classification/${documentId}`);
}

// Function to create a draw template from a document
async function createDrawTemplate(documentId) {
  try {
    console.log('Creating draw template for document:', documentId);
    
    const templateData = {
      name: `Draw Template - ${new Date().toLocaleDateString()}`,
      description: 'Auto-generated draw template from uploaded document',
      draw_number: 1
    };
    
    const url = `${apiBase}documents/${documentId}/create_draw_template`;
    const response = await axios.post(url, { template: templateData }, authToken.value);
    
    console.log('Draw template creation response:', response.data);
    
    if (response.data.success && response.data.template) {
      // Navigate to the template setup page
      router.push(`/draw-template-setup/${response.data.template.id}`);
    } else {
      errorStore.setError('Template Creation', response.data.error || 'Failed to create template', 'error', false);
    }
    
  } catch (error) {
    console.error('Error creating draw template:', error);
    errorStore.setError('Template Creation', 'Failed to create draw template', 'error', false);
  }
}

// Function to trigger OCR processing for a document
async function triggerOcrProcessing(documentId) {
  try {
    console.log('Triggering OCR processing for document:', documentId);
    
    // Call the OCR processing endpoint
    const url = `${apiBase}documents/${documentId}/process`;
    const response = await axios.post(url, {}, authToken.value);
    
    console.log('OCR processing response:', response.data);
    
    // Update the document status after a short delay to simulate processing
    setTimeout(async () => {
      // Find the document in the list
      const docIndex = documentsList.value.findIndex(doc => doc.id === documentId);
      if (docIndex !== -1) {
        // Update the status to completed
        documentsList.value[docIndex].status = 'completed';
        
        // Update the grid if available
        if (gridApi.value && !gridApi.value.isDestroyed()) {
          gridApi.value.setRowData(documentsList.value);
          gridApi.value.refreshCells();
        }
      }
      
      // Also fetch from server to ensure we have the latest data
      await fetchDocumentList();
    }, 3000); // 3 second delay to simulate processing
    
    return true;
  } catch (error) {
    console.error('Error triggering OCR processing:', error);
    errorStore.setError('Document Processing', 'Failed to process document', 'error', false);
    return false;
  }
}

const expandCollapseController = (element) => {
  switch (element) {
    case 'projectDetails':
      isProjectDetailsOpen.value = !isProjectDetailsOpen.value
      break;
    case 'baselineDocuments':
      isBaselineDocumentUpload.value = !isBaselineDocumentUpload.value
      break;
    case 'drawDocuments':
      isDrawDocumentUpload.value = !isDrawDocumentUpload.value
      break;
    case 'projectDocumentsList':
      isProjectDocumentsListOpen.value = !isProjectDocumentsListOpen.value
      break;
    default:
      break;
  }
}

/** API Calls for Upload */
const apiBase = process.env.VUE_APP_ENV_API_BASE || "http://localhost:3001/api/v1/"
const authToken = ref({});
const authStore = useAuthStore();
const errorStore = useErrorStore();
const enteredProjectId = ref(null);
const documentUploadCtr = ref(0);

// Upload progress state
const isUploading = ref(false);
const uploadingMessage = ref('');
const uploadedCount = ref(0);
const totalFiles = ref(0);

// G703 Template Modal state
const showG703TemplateModal = ref(false);
const selectedProjectName = ref('');
const g703SampleFileName = ref('');
const pendingG703Files = ref([]);

// For Project Upload 
async function uploadedBaselineDocument(projectId, data) {
  // Iterate in each container
  // Developer Budget Files first
  await uploadDocuments(projectId, data.developerBudget?.values, 'DeveloperBudget', data.date, null);
  // General Contractor Budget Files
  await uploadDocuments(projectId, data.generalContractorBudget?.values, 'GeneralContractorBudget', data.date, null);
  // General Contractor Schedule Files
  await uploadDocuments(projectId, data.generalContractorSchedule?.values, 'GeneralContractorSchedule', data.date, null);
  // Executed Construction Contracts
  await uploadDocuments(projectId, data.executedConstructionContracts?.values, 'ConstructionContracts', data.date, null);
  
  // Show document list after uploads
  isProjectDocumentsListOpen.value = true;
  // Increment the counter to force the document list to refresh
  documentUploadCtr.value++;
}

async function uploadFile(file, document) {
  try {
    // Use the document type selected by user in Step 1 (passed in document object)
    // NEVER use filename detection - respect user's explicit choice
    const documentType = document.document_type || detectDocumentType(file.name);
    console.log('📋 Processing document with user-selected type:', documentType, 'for file:', file.name);
    
    // Process ALL document types that user selects - respect their choice
    console.log('📄 Processing document type:', documentType, 'as selected by user');
    
    let keys = Object.keys(document);
    let formData = new FormData();
    formData.append('document[file]', file);
    keys.forEach((key) => formData.append(`document[${key}]`, document[key]));
    
    // Add auto-processing for production
    // 🔥 DISABLED: Changed to 'false' so ALL documents go through page selection flow
    // This prevents invoices and other PDFs from being auto-processed without user review
    formData.append('document[auto_process]', 'false');
    
    // Enhanced processing for G703 documents
    if (documentType === 'g703') {
      // Use Template 33 (Standard G703 Mapping) for template-aware processing
      const defaultG703TemplateId = 33; // Template 33 with complete 18-column mapping
      formData.append('document[template_id]', defaultG703TemplateId);
      console.log('✅ G703 document detected - using Template 33 for enhanced OCR processing');
    }
    
    // Enhanced processing for Excel/Budget documents
    if (documentType === 'budget' || file.name.toLowerCase().includes('.xls')) {
      console.log('✅ Excel/Budget document detected - will use enhanced XLS processor');
    }

    // Use the correct API base URL
    const uploadUrl = `${apiBase}documents`;
    console.log('Uploading valuable document to:', uploadUrl);
    console.log('File data:', file.name, file.size, file.type);
    if (documentType === 'g703') {
      console.log('G703 template-aware processing enabled');
    }
    
    // Let's try a direct axios call instead of using axiosPostQuery
    try {
      const config = {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      };
      
      // Add auth headers if available
      if (authToken.value && authToken.value.headers) {
        Object.assign(config.headers, authToken.value.headers);
      }
      
      const response = await axios.post(uploadUrl, formData, config);
      
      console.log('Upload response:', response.data);
      
      // Check if this is a regular user upload (not admin)
      if (response.data.is_admin_upload === false) {
        // Regular user: Show success message and don't continue to manual processing
        alert('✅ Upload successful! This draw will be populated in the project when AI processing finishes. You will be notified when it\'s ready.');
        // Close the upload modal by emitting an event or setting a flag
        // The parent component should handle closing the modal
        return true;
      }
      
      // Admin user: Continue with normal manual processing flow
      isProjectDocumentsListOpen.value = true;
      
      // Create a document object from the response
      const newDocument = {
        id: response.data.document_id || Date.now(),
        filepath: response.data.filename || file.name,
        status: 'in_progress', // Set status to in_progress initially
        project_id: document.project_id || enteredProjectId.value,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        document_type: document.document_type || 'uploaded'
      };
      
      // Trigger OCR processing for the uploaded document
      triggerOcrProcessing(newDocument.id);
      
      // Add the new document to the local list
      documentsList.value.push(newDocument);
      
      // Increment the counter to force the document list to refresh
      documentUploadCtr.value++;
      console.log('Document upload counter incremented:', documentUploadCtr.value);
      
      // Force refresh the document list by fetching the latest data
      if (gridApi.value && !gridApi.value.isDestroyed()) {
        // Update the grid with the local document list
        gridApi.value.setRowData(documentsList.value);
        gridApi.value.refreshCells();
        
        // Also fetch from server to ensure we have the latest data
        await fetchDocumentList();
      }
      
      return true;
    } catch (axiosError) {
      console.error('Axios error details:', {
        message: axiosError.message,
        status: axiosError.response?.status,
        statusText: axiosError.response?.statusText,
        responseData: axiosError.response?.data,
        config: axiosError.config
      });
      
      errorStore.setError('Document Upload', `Upload failed: ${axiosError.message}`, 'error', false);
      return false;
    }
  } catch (error) {
    console.error('Error in uploadFile function:', error);
    errorStore.setError('Document Upload', 'An error occurred. The document upload was unsuccessful.', 'error', false);
    return false;
  }
}

async function uploadedDrawDocument(projectId, data) {
  // Iterate in each container
  // Borrower Requisition Letter
  await uploadDocuments(projectId, data.borrowerRequisitionLetter?.values, 'BorrowerRequisitionLetter', data.periodThroughDate, data.drawNumber);
  // Developer Draw Tracker
  await uploadDocuments(projectId, data.developerDrawTracker?.values, 'SponsorDrawTracker', data.periodThroughDate, data.drawNumber);
  // Full Payment Application
  await uploadDocuments(projectId, data.fullPaymentApplication?.values, 'FullPaymentApplication', data.periodThroughDate, data.drawNumber);
  // Soft Cost Invoices
  await uploadDocuments(projectId, data.softCostInvoices?.values, 'SoftCostInvoices', data.periodThroughDate, data.drawNumber);
  
  // Show document list after uploads
  isProjectDocumentsListOpen.value = true;
  // Increment the counter to force the document list to refresh
  documentUploadCtr.value++;
}

async function uploadDocuments(projectId, documentContainerArray, documentContainerName, date, drawNumber) {
  // If no documents, return early
  if (!documentContainerArray || !Array.isArray(documentContainerArray) || documentContainerArray.length === 0) {
    console.log(`No documents to upload for ${documentContainerName}`);
    return;
  }
  
  const validDocuments = [...documentContainerArray];
  const convertedDate = formattedDate(date);
  const convertedDrawNumber = drawNumber ? '_' + drawNumber : '';
  
  console.log(`Uploading ${validDocuments.length} documents for ${documentContainerName}`);
  
  for (const file of validDocuments) {
    if (!file || !file.name) {
      console.warn('Invalid file object found, skipping', file);
      continue;
    }
    
    const validFile = getFilenameAndExtension(file.name);
    if (!validFile) {
      console.warn(`Could not extract filename and extension for ${file.name}, skipping`);
      continue;
    }
    
    let document = {
      filepath: validFile[0] + '_' + documentContainerName + '_' + convertedDate + `${convertedDrawNumber}` + validFile[1],
      status: 'in_progress',
      project_id: projectId,
      period_through: convertedDate,
      draw_number: drawNumber
    };

    try {
      await uploadFile(file, document);
      console.log(`Successfully uploaded ${file.name} for ${documentContainerName}`);
    } catch (error) {
      console.error(`Failed to upload ${file.name} for ${documentContainerName}`, error);
    }
  }
}

const getFilenameAndExtension = (filename) => {
  const fileNameWithExtension = filename;

  const lastDotIndex = fileNameWithExtension.lastIndexOf('.');
  if (lastDotIndex !== -1) {
    const fileName = fileNameWithExtension.substring(0, lastDotIndex);
    const fileExtension = fileNameWithExtension.substring(lastDotIndex);
    return [fileName, fileExtension]
  } else {
    return null
  }
}

// Helper function to detect document type from filename
function detectDocumentType(filename) {
  const lowerName = filename.toLowerCase();
  if (lowerName.includes('g703') || lowerName.includes('continuation')) {
    return 'g703';
  } else if (lowerName.includes('g702') || lowerName.includes('application')) {
    return 'g702';
  } else if (lowerName.includes('budget') || lowerName.includes('excel') || lowerName.includes('.xlsx') || lowerName.includes('.xls')) {
    return 'budget';
  } else {
    return 'unknown';
  }
}

function formattedDate(date) {
  const dateObj = new Date(date);
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, '0'); // Months are zero-based
  const day = String(dateObj.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

let projectBaselineDocuments = ref(null);
let projectDrawDocuments = ref(null);
const hasBaselineFiles = ref(false);
const hasDrawFiles = ref(false);

function updateBaselineFiles(files) {
  hasBaselineFiles.value = files && files.length > 0;
}

function updateDrawFiles(files) {
  console.log('Draw files received:', files);
  hasDrawFiles.value = files && files.length > 0;
  console.log('hasDrawFiles updated to:', hasDrawFiles.value);
}

function directFileUpdate(files, containerName) {
  console.log(`Direct file update for ${containerName}:`, files);
  hasDrawFiles.value = files && files.length > 0;
  console.log('hasDrawFiles updated directly to:', hasDrawFiles.value);
}

async function submitBaselineDocuments() {
  try {
    console.log('Submitting baseline documents...');
    
    if (!projectBaselineDocuments.value || !projectBaselineDocuments.value.localForm) {
      console.error('No baseline documents form data available');
      errorStore.setError('Document Upload', 'No document data available to upload.', 'error', false);
      return;
    }
    
    if (!enteredProjectId.value) {
      console.error('No project ID selected');
      errorStore.setError('Document Upload', 'Please select a project before uploading documents.', 'error', false);
      return;
    }
    
    const projectBaselineDocumentsFormData = toRaw(projectBaselineDocuments.value.localForm);
    console.log('Form data:', projectBaselineDocumentsFormData);
    
    // Show loading indicator
    isUploading.value = true;
    uploadingMessage.value = 'Uploading baseline documents to server...';
    uploadedCount.value = 0;
    
    // Count total files
    totalFiles.value = (projectBaselineDocumentsFormData.partnershipAgreement?.values?.length || 0) +
                       (projectBaselineDocumentsFormData.constructionBudget?.values?.length || 0) +
                       (projectBaselineDocumentsFormData.drawSchedule?.values?.length || 0);
    
    await uploadedBaselineDocument(enteredProjectId.value, projectBaselineDocumentsFormData);
    console.log('Baseline documents uploaded successfully');
    
    // Hide loading indicator
    isUploading.value = false;
    uploadingMessage.value = '';
    
  } catch (error) {
    console.error('Error submitting baseline documents:', error);
    // Hide loading indicator on error
    isUploading.value = false;
    uploadingMessage.value = '';
    errorStore.setError('Document Upload', 'An error occurred while uploading baseline documents.', 'error', false);
  }
}

async function submitDrawDocuments() {
  try {
    console.log('Submitting draw documents...');
    
    if (!projectDrawDocuments.value || !projectDrawDocuments.value.localForm) {
      console.error('No draw documents form data available');
      errorStore.setError('Document Upload', 'No document data available to upload.', 'error', false);
      return;
    }
    
    if (!enteredProjectId.value) {
      console.error('No project ID selected');
      errorStore.setError('Document Upload', 'Please select a project before uploading documents.', 'error', false);
      return;
    }
    
    const projectDrawDocumentsFormData = toRaw(projectDrawDocuments.value.localForm);
    console.log('Form data:', projectDrawDocumentsFormData);
    
    // Check for G703 files in the uploaded documents
    const g703Files = findG703Files(projectDrawDocumentsFormData);
    console.log('G703 files found:', g703Files);
    
    if (g703Files.length > 0) {
      console.log('G703 files detected, showing template modal');
      // Store the form data and G703 files for later processing
      pendingG703Files.value = g703Files;
      g703SampleFileName.value = g703Files[0].name;
      
      // Show G703 template configuration modal
      showG703TemplateModal.value = true;
      return; // Don't proceed with upload until template is configured
    }
    
    console.log('No G703 files found, proceeding with normal upload');
    
    // Show loading indicator
    isUploading.value = true;
    uploadingMessage.value = 'Uploading draw documents to server...';
    uploadedCount.value = 0;
    
    // Count total files
    totalFiles.value = (projectDrawDocumentsFormData.borrowerRequisitionLetter?.values?.length || 0) +
                       (projectDrawDocumentsFormData.developerDrawTracker?.values?.length || 0) +
                       (projectDrawDocumentsFormData.fullPaymentApplication?.values?.length || 0) +
                       (projectDrawDocumentsFormData.softCostInvoices?.values?.length || 0);
    
    // No G703 files, proceed with normal upload
    await uploadedDrawDocument(enteredProjectId.value, projectDrawDocumentsFormData);
    console.log('Draw documents uploaded successfully');
    
    // Hide loading indicator
    isUploading.value = false;
    uploadingMessage.value = '';
    
    // Track in Datadog
    if (datadogRum) {
      datadogRum.addAction('draw_documents_uploaded', {
        project_id: enteredProjectId.value,
        has_g703: false
      });
    }
    
  } catch (error) {
    console.error('Error submitting draw documents:', error);
    // Hide loading indicator on error
    isUploading.value = false;
    uploadingMessage.value = '';
    errorStore.setError('Document Upload', 'An error occurred while uploading draw documents.', 'error', false);
    
    // Track error in Datadog
    if (datadogRum) {
      datadogRum.addError(error, { context: 'draw_documents_upload_failed' });
    }
  }
}

/** Document List Functions */
const documentsUrl = computed(() => {
  return `documents?project_id=${enteredProjectId.value}`
})

// Reference to the AgGrid API
const gridApi = ref(null);

// Local array to store uploaded documents
const documentsList = ref([]);

// Function to fetch the latest document list from the server
async function fetchDocumentList() {
  try {
    console.log('Fetching latest document list...');
    const url = `${apiBase}documents?project_id=${enteredProjectId.value}`;
    
    const response = await axios.get(url, authToken.value);
    if (response.data) {
      console.log('Fetched document list:', response.data);
      
      // Update the local documents list
      documentsList.value = response.data;
      
      // If we have a reference to the grid API, update the row data
      if (gridApi.value && !gridApi.value.isDestroyed()) {
        gridApi.value.setRowData(documentsList.value);
        gridApi.value.refreshCells();
      }
    }
  } catch (error) {
    console.error('Error fetching document list:', error);
    errorStore.setError('Document List', 'Failed to refresh document list', 'error', false);
  }
}

const documentsColumns = ['id', 'filepath', 'status', 'period_through', 'draw_number', 'project_id', 'error', 'created_at', 'updated_at', 'actions']
const documentsColumnDefs = computed(() => {
  let documentsColumnDefs = []
  documentsColumns.forEach(columnName => {
    let opts = {
      headerName: columnName,
      field: columnName,
      editable: false,
      filter: true,
      floatingFilter: true,
      minWidth: 50,
    }

    if(columnName === 'filepath') {
      opts.minWidth = 500
      opts.tooltipField = 'filepath'; // Enable tooltip for this column
      opts.tooltipValueGetter = (params) => params.value; // Show the value of the filepath on hover
    }
    
    // Customize the status column to show a loading indicator
    if(columnName === 'status') {
      opts.cellRenderer = function(params) {
        if (params.value === 'in_progress') {
          return `<div class="status-indicator processing">
                    <span class="loading-spinner"></span> Processing
                  </div>`;
        } else if (params.value === 'completed') {
          return `<div class="status-indicator completed">
                    <span class="check-icon">✓</span> Completed
                  </div>`;
        } else if (params.value === 'failed') {
          return `<div class="status-indicator failed">
                    <span class="error-icon">✗</span> Failed
                  </div>`;
        }
        return params.value;
      };
    }
    
    // Add actions column with review button
    if(columnName === 'actions') {
      opts.headerName = 'Actions';
      opts.filter = false;
      opts.floatingFilter = false;
      opts.minWidth = 120;
      opts.cellRenderer = function(params) {
        if (!params.data) return '';
        
        // Check if this is a draw tracker document or Excel file that could be a draw document
        const isDrawTracker = params.data.filepath && 
          (params.data.filepath.includes('SponsorDrawTracker') || 
           params.data.filepath.includes('Draw') ||
           params.data.filepath.includes('Tracker') ||
           params.data.filepath.includes('.xlsx') ||
           params.data.filepath.includes('.xls') ||
           params.data.filepath.includes('Budget') ||
           params.data.filepath.includes('G703'));
        
        // Show different content based on document status
        if (params.data.status === 'completed') {
          // Show buttons for completed documents
          let buttons = `
            <div class="action-buttons">
              <button class="classify-button" data-document-id="${params.data.id}">Classify</button>
              <button class="review-button" data-document-id="${params.data.id}">Review</button>
          `;
          
          // Add Create Template button for draw tracker documents
          if (isDrawTracker) {
            buttons += `<button class="template-button" data-document-id="${params.data.id}">Create Template</button>`;
          }
          
          buttons += `</div>`;
          return buttons;
        } else if (params.data.status === 'in_progress') {
          // Show a message for documents being processed
          return `<div class="processing-message">Processing document...</div>`;
        } else if (params.data.status === 'failed') {
          // Show a retry button for failed documents
          return `
            <div class="action-buttons">
              <button class="retry-button" data-document-id="${params.data.id}">Retry</button>
            </div>
          `;
        }
        return '';
      };
      opts.cellRendererParams = {
        clicked: function(field, event) {
          const target = event.target;
          const documentId = target.getAttribute('data-document-id');
          
          if (target.classList.contains('review-button')) {
            navigateToDocumentReview(documentId);
          } else if (target.classList.contains('classify-button')) {
            navigateToDocumentClassification(documentId);
          }
        }
      };
      opts.onCellClicked = function(params) {
        const target = params.event.target;
        if (!target.getAttribute) return;
        
        const documentId = target.getAttribute('data-document-id');
        if (!documentId) return;
        
        if (target.classList.contains('review-button')) {
          navigateToDocumentReview(documentId);
        } else if (target.classList.contains('classify-button')) {
          navigateToDocumentClassification(documentId);
        } else if (target.classList.contains('template-button')) {
          createDrawTemplate(documentId);
        } else if (target.classList.contains('retry-button')) {
          // Retry OCR processing for failed documents
          triggerOcrProcessing(documentId);
        }
      };
    }
    
    // freeze/pin itemHierarchy to the left so it can be seen when scrolling (esp. for Schedule page)
    if (columnName === 'id' || columnName === 'dda_term_id' || columnName === 'project_id' || columnName === 'projectable_id') {
      opts.pinned = 'left'
    }
    documentsColumnDefs.push(opts)
  })
  return documentsColumnDefs
})


// Function to handle the grid-ready event from AgGrid
function onDocumentGridReady(params) {
  console.log('Document grid is ready');
  // Store the grid API reference
  if (params && params.api) {
    gridApi.value = params.api;
    console.log('Grid API reference stored');
  }
}

onMounted(async () => {
  authToken.value = authStore.getAuthTokenHeader;
  await callAllProjects();
  
  // Initialize the documents list with an empty array
  documentsList.value = [];
})

// For AutoComplete
const allProjects = ref([]);

async function callAllProjects() {
  try {
    console.log('Fetching all projects...');
    const response = await axios.get(`${apiBase}projects`, authToken.value);
    
    if (response.data) {
      allProjects.value = response.data;
      console.log('Projects loaded:', allProjects.value.length);
    }
  } catch (error) {
    console.error('Error fetching projects:', error);
    errorStore.setError('Projects', 'Failed to load projects', 'error', false);
  }
}

function updateSelectedProject(value) {
  enteredProjectId.value = value.id;
  selectedProjectName.value = value.name;
  
  // Clear the documents list
  documentsList.value = [];
  
  // Fetch the document list for the selected project
  if (enteredProjectId.value) {
    fetchDocumentList();
  }
}

function clearSearch() {
  enteredProjectId.value = null;
  selectedProjectName.value = '';
  
  // Clear the documents list
  documentsList.value = [];
  
  // Update the grid if available
  if (gridApi.value && !gridApi.value.isDestroyed()) {
    gridApi.value.setRowData([]);
    gridApi.value.refreshCells();
  }
}

// G703 Template Modal Functions
function findG703Files(formData) {
  const g703Files = [];
  console.log('Searching for G703 files in form data:', formData);
  
  // Check all file containers for G703 files
  const containers = ['borrowerRequisitionLetter', 'developerDrawTracker', 'fullPaymentApplication', 'softCostInvoices'];
  
  containers.forEach(containerName => {
    console.log(`Checking container: ${containerName}`);
    if (formData[containerName] && formData[containerName].values) {
      console.log(`Container ${containerName} has ${formData[containerName].values.length} files`);
      formData[containerName].values.forEach(file => {
        console.log(`Checking file: ${file.name}`);
        if (isG703File(file)) {
          console.log(`G703 file detected: ${file.name}`);
          g703Files.push(file);
        }
      });
    } else {
      console.log(`Container ${containerName} is empty or undefined`);
    }
  });
  
  console.log(`Total G703 files found: ${g703Files.length}`);
  return g703Files;
}

function isG703File(file) {
  const fileName = file.name.toLowerCase();
  const isG703 = fileName.includes('g703') || 
                 fileName.includes('payment application') ||
                 fileName.includes('application for payment') ||
                 fileName.includes('aia g703') ||
                 fileName.includes('aia') ||
                 fileName.includes('703') ||
                 fileName.includes('continuation') ||
                 fileName.includes('pay app') ||
                 fileName.includes('payapp');
  
  console.log(`File "${fileName}" is G703: ${isG703}`);
  return isG703;
}

function closeG703TemplateModal() {
  showG703TemplateModal.value = false;
  pendingG703Files.value = [];
  g703SampleFileName.value = '';
}

async function handleG703TemplateSaved() {
  try {
    // Close the modal
    closeG703TemplateModal();
    
    // Now proceed with the original upload
    const projectDrawDocumentsFormData = toRaw(projectDrawDocuments.value.localForm);
    await uploadedDrawDocument(enteredProjectId.value, projectDrawDocumentsFormData);
    console.log('Draw documents uploaded successfully after G703 template configuration');
    
  } catch (error) {
    console.error('Error uploading draw documents after G703 template configuration:', error);
    errorStore.setError('Document Upload', 'An error occurred while uploading draw documents.', 'error', false);
  }
}

</script>

<style scoped lang="scss">
@import "./src/assets/main.scss";

/* Status indicator styles */
.status-indicator {
  display: flex;
  align-items: center;
  font-weight: 500;
  
  &.processing {
    color: #f39c12;
  }
  
  &.completed {
    color: #2ecc71;
  }
  
  &.failed {
    color: #e74c3c;
  }
  
  .loading-spinner {
    display: inline-block;
    width: 12px;
    height: 12px;
    border: 2px solid #f39c12;
    border-radius: 50%;
    border-top-color: transparent;
    margin-right: 5px;
    animation: spin 1s linear infinite;
  }
  
  .check-icon, .error-icon {
    margin-right: 5px;
  }
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Action button styles */
.action-buttons {
  display: flex;
  gap: 8px;
}

.review-button, .classify-button {
  border: none;
  border-radius: 4px;
  padding: 4px 8px;
  cursor: pointer;
  font-size: 12px;
  color: white;
}

.review-button, .classify-button, .template-button {
  border: none;
  border-radius: 4px;
  padding: 4px 8px;
  cursor: pointer;
  font-size: 12px;
  color: white;
}

.review-button {
  background-color: #0366d6;
}

.review-button:hover {
  background-color: #0256b9;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.classify-button {
  background-color: #28a745;
}

.classify-button:hover {
  background-color: #218838;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.template-button {
  background-color: #6f42c1;
}

.template-button:hover {
  background-color: #5a2d91;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.tile {
  margin: 5px 5px 5px 0;
  border-radius: 8px;
  box-sizing: border-box;
  padding: 20px;
  height: 100%;

  .project-detail-header {
    color: $navyBlue;
    font-size: 14px;
    font-weight: bold;
    padding-bottom: 5px;
  }

  .project-detail-sub-header {
    color: $navyBlue;
    font-size: 12px;
    font-weight: bold;
    padding: 5px 0px;
  }

}

.existing-project {
  grid-column: 1 / 3; // span width of whole screen
  height: auto;
  margin-right: 20px;
}

.page-description {
  font-size: 14px;
  width: 100%;
  padding: 15px 0px;
}

.label-text-spacing {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;

  div {
    display: flex;
    align-items: center;
    justify-content: flex-end;
  }
}


.divider {
  border-top: 2px solid lightgray;
}

/** Container Accordion -- Start */
.accordion-panel-header {
  display: flex;
  align-items: center;
  position: relative;
  width: 99%;
  margin: 15px 0;
  color: $navyBlue;
  font-weight: bolder;

  label {
    font-size: 20px;
  }
}

// Button
.action-btn-container {
  display: flex;
  justify-content: flex-end;
  margin-top: 15px;
}

.primary-action-button {
  background-color: $mediumBlue2;
  color: white;
  border: 0;
  border-radius: 4px;
  margin: 8px;
  cursor: pointer;
  padding: 10px;
  font-size: 12px;

  &.disabled {
    background-color: #ccc;
    color: #666;
    cursor: not-allowed;
    opacity: 0.6;
  }
}

/** Button */
.project-action-button {
  display: flex;
  justify-content: flex-end;
  padding: 15px 0;

  button {
    background-color: #cccccc; /* Gray for disabled state */
    color: #666666;
    border: none;
    border-radius: 5px;
    padding: 10px 20px;
    cursor: not-allowed;
    font-size: 12px;
    transition: all 0.3s ease;
  }

  button.active-button {
    background-color: $brightBlue1;
    color: white;
    cursor: pointer;
  }

  button.active-button:hover {
    background-color: darken($brightBlue1, 10%);
  }
}

.custom-text-input {
  display: flex;
  align-items: center;
  font-size: 12px;
  padding: 5px 0px;
  width: 100%;

  .left-div {
    width: 40%;
  }

  .right-div {
    display: flex;
    justify-content: flex-end;
    flex: 1;
  }

  input[type=text] {
    color: $brightBlue1;
    background-color: $lightGray1;
    border: 1px solid $lightGray1;
    border-radius: 5px;
    padding: 5px;
    height: 10px;
    width: 100%;
  }

  input[type=text]:focus {
    border-color: $brightBlue1;
    outline: $brightBlue1;
  }

  input[type=text]:disabled {
    cursor: not-allowed;
  }
}

.project-id {
  display: flex;
  justify-content: flex-start !important;
  flex: 1;
  color: $navyBlue;
}

.documents-link-container {
  margin-top: 15px;
  padding: 12px 16px;
  background: linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%);
  border: 1px solid #e1bee7;
  border-radius: 8px;
  text-align: center;
}

.documents-link {
  color: #4a5fd5;
  text-decoration: none;
  font-weight: 600;
  font-size: 14px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
  padding: 4px 8px;
  border-radius: 4px;
}

.documents-link:hover {
  color: #3d4ed8;
  background: rgba(74, 95, 213, 0.1);
  text-decoration: none;
  transform: translateX(4px);
}

/* Upload Progress Overlay Styles */
.upload-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
}

.upload-modal {
  background: white;
  padding: 40px 60px;
  border-radius: 12px;
  text-align: center;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
  max-width: 500px;
}

.upload-spinner {
  width: 60px;
  height: 60px;
  border: 6px solid #f3f3f3;
  border-top: 6px solid #4A5FD5;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 24px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.upload-modal h3 {
  font-size: 24px;
  color: #2c3e50;
  margin: 0 0 16px 0;
  font-weight: 600;
}

.upload-progress-text {
  font-size: 16px;
  color: #666;
  margin: 0 0 12px 0;
  line-height: 1.5;
}

.upload-count {
  font-size: 14px;
  color: #4A5FD5;
  font-weight: 500;
  margin: 0;
}
</style>
